import { AxiosResponse } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import {
  GetSystemResponse,
  Part,
  UpdateSystemRequest,
  GetGroupedPartCategoriesResponse,
  Group,
  CreateGroupRequest,
  CreatePartRequest,
  CreateManifoldRequest,
  OverlapGroupLinkResponse,
  VirtualGroup,
  PartLink,
  CreatePartLinkRequest,
  PartCategoryToAdd,
  LinkState,
  RevertManifoldRequest,
  CreateManifoldResponse,
  CreateSystemRequest,
  UpdateOverlapGroupLinkRequest,
  UpdateOverlapGroupLinkResponse,
  UpdateWorkflowRequest,
  WorkflowStepStatus,
  Metadata,
  AddValveToManifoldRequest,
  SaveSystemSharesRequest,
} from '../models/Api';
import ApiService from './ApiService';
import { isManifoldOptimized, sortByOrder } from '../utils/Utils';
import { BehaviorSubject } from 'rxjs';
export type SystemState = {
  system?: GetSystemResponse;
};

const initialState: SystemState = {
  system: undefined,
};

const state$ = new BehaviorSubject<SystemState>(initialState);

const getState = () => {
  return state$.getValue();
};

const setState = (data: SystemState) => {
  return state$.next({ ...data });
};

const buildUpdateData = (system: GetSystemResponse | undefined) => {
  return {
    id: system?.id as string,
    name: system?.name as string,
    description: system?.description,
    displayInMetricUnits: system?.displayInMetricUnits,
    isRightToLeft: system?.isRightToLeft,
  };
};

function getUpdatedGroupInState(id: number) {
  const subGroups = getState()?.system?.groups?.filter((group) => group.parentGroupId === id) as Group[];
  subGroups?.forEach((subGroup) => getUpdatedGroupInState(subGroup.id as number));

  const system = getState()?.system as GetSystemResponse;
  const groups = system.groups?.filter((group) => group.id !== id) as Group[];
  const groupLinks = system.groupLinks?.filter((groupLink) => groupLink.startId !== id || groupLink.endId !== id);
  const parts = system.parts?.filter((part) => part.groupInstanceId !== id) as Part[];

  const partLinks: PartLink[] = [];
  for (const part of parts) {
    const partLinksToKeep = system.partLinks?.filter((partLink) => partLink.startId === part.id || partLink.endId === part.id) as PartLink[];
    partLinksToKeep.forEach((partLink) => {
      if (!partLinks.includes(partLink)) partLinks.push(partLink);
    });
  }

  const overlapGroupLinks: OverlapGroupLinkResponse[] = [];
  for (const group of groups) {
    const overlapGroupLinksToKeep = system.overlapGroupLinks?.filter(
      (overlapGroupLink) => overlapGroupLink.intersectedGroupId === group.id,
    ) as OverlapGroupLinkResponse[];
    overlapGroupLinksToKeep.forEach((overlapGroupLink) => {
      if (!overlapGroupLinks.includes(overlapGroupLink) && overlapGroupLink.overlappingGroupId !== id) overlapGroupLinks.push(overlapGroupLink);
    });
  }

  const overlapGroupLinkIds = overlapGroupLinks.map((overlapGroupLink) => overlapGroupLink.id as number);
  const virtualGroups: VirtualGroup[] = [];
  for (const virtualGroup of system.virtualGroups as VirtualGroup[]) {
    virtualGroup.overlapGroupLinkIds = virtualGroup.overlapGroupLinkIds?.filter((overlapGroupLinkId) => overlapGroupLinkIds.includes(overlapGroupLinkId));
    if (
      virtualGroup.baseGroupId != id &&
      groups.map((group) => group.id as number).includes(virtualGroup.intersectedGroupId as number) &&
      virtualGroup.overlapGroupLinkIds?.length &&
      !virtualGroups.includes(virtualGroup)
    ) {
      virtualGroups.push(virtualGroup);
    }
  }

  const systemCopy = { ...getState().system };
  systemCopy.groups = groups;
  systemCopy.groupLinks = groupLinks;
  systemCopy.parts = parts;
  systemCopy.partLinks = partLinks;
  systemCopy.overlapGroupLinks = overlapGroupLinks;
  systemCopy.virtualGroups = virtualGroups;
  return systemCopy;
}

function deleteManifold(id: number, withApplicationGroups: boolean) {
  const systemCopy = { ...getState().system };
  /** REMOVING SEGMENT ELEMENTS */
  /** Get the manifold segment */
  const manifoldSegmentGroups = systemCopy.groups?.filter((group) => group.parentGroupId === id) as Group[];
  manifoldSegmentGroups.forEach((segmentGroup) => {
    /** Removing the virtual group corresponding to the deleted manifold segment   */
    systemCopy.virtualGroups = systemCopy.virtualGroups?.filter((group) => group.intersectedGroupId !== segmentGroup.id);
    /** Removing the overlapgroup links corresponding to the deleted manifold segment   */
    systemCopy.overlapGroupLinks = systemCopy.overlapGroupLinks?.filter((group) => group.intersectedGroupId !== segmentGroup.id);
    /** Removing the parts corresponding to the deleted manifold segment   */
    systemCopy.parts = systemCopy.parts?.filter((part) => part.groupInstanceId !== segmentGroup.id);
    /** Removing the groups corresponding to the deleted manifold segment   */
    systemCopy.groups = systemCopy.groups?.filter((group) => group.id !== segmentGroup.id);
  });
  /** Removing the parts belonging to the manifold master group */
  systemCopy.parts = systemCopy.parts?.filter((part) => part.groupInstanceId !== id) as Part[];
  /** Removing the manifold group from group array */
  systemCopy.groups = systemCopy.groups?.filter((group) => group.id !== id) as Group[];
  /** REMOVING APPLICATION GROUP ELEMENTS */
  if (withApplicationGroups) {
    const overlapGroupLinks: OverlapGroupLinkResponse[] = [];
    manifoldSegmentGroups?.map((segmentGroup) => {
      getState().system?.overlapGroupLinks?.map((overlapGroupLink) => {
        if (overlapGroupLink.intersectedGroupId === segmentGroup.id) overlapGroupLinks.push(overlapGroupLink as OverlapGroupLinkResponse);
      });
    });
    overlapGroupLinks.forEach((overlapGroupId) => {
      /** Removing the parts belonging to the overlapping group */
      systemCopy.parts = systemCopy.parts?.filter((part) => part.groupInstanceId !== overlapGroupId.overlappingGroupId);
      /** Removing the application groups from group array */
      systemCopy.groups = systemCopy.groups?.filter((group) => group.id !== overlapGroupId.overlappingGroupId);
    });
  }
  setState({ system: systemCopy });
}

const SystemService = {
  getCurrentState: () => {
    return state$.value;
  },
  getState: (setSystemState: Dispatch<SetStateAction<SystemState>>) => {
    return state$.asObservable().subscribe((next) => setSystemState(next));
  },

  fetchSystemById: async (systemId: string) => {
    const api = ApiService.getApi();
    const response = await api.systemsDetail(systemId);
    if (response.status === 200) {
      setState({ system: response.data });
    }
  },

  fetchFakeSystem: (name: string): void => {
    const api = ApiService.getApi();
    api.manifoldsFakesDetail(name).then((response: AxiosResponse) => {
      setState({ system: response.data });
    });
  },

  refreshSystemById: async (systemId: string): Promise<boolean> => {
    const currentSystem = getState().system as GetSystemResponse;
    if (currentSystem.id !== systemId) return false;
    await SystemService.fetchSystemById(systemId);
    return true;
  },

  updateLinkByEndPartId: async (partId: string) => {
    const currentSystem = getState().system as GetSystemResponse;
    const partLink = currentSystem.partLinks?.find((link) => link.endId === partId);
    if (partLink) {
      const api = ApiService.getApi();
      const updatedPartLinkResponse = await api.partLinksDetail(partLink.id as number);
      if (updatedPartLinkResponse.status === 200) {
        Object.assign(partLink, updatedPartLinkResponse.data);
      }
    }
  },

  refreshPartById: async (systemId: string, partId: string) => {
    const currentSystem = getState().system as GetSystemResponse;
    if (currentSystem.id !== systemId) return false;
    await SystemService.getPartsDetail(partId);
    await SystemService.updateLinkByEndPartId(partId);
    return true;
  },

  checkSystemMatch: (systemId: string): boolean => {
    const currentSystem = getState().system as GetSystemResponse;
    return currentSystem.id === systemId;
  },

  update: async (updatedData: UpdateSystemRequest) => {
    const api = ApiService.getApi();
    const response = await api.systemsUpdate(updatedData.id, updatedData);
    if (response.data.updated) {
      const systemCopy = Object.assign(getState().system as GetSystemResponse, updatedData);
      systemCopy.name = response.data.name;
      setState({ system: systemCopy });
    }
    return response.data.updated;
  },

  clearSystemData: (): void => {
    setState({});
  },

  createPartSizingData: async (part: Part) => {
    const partId = part.id as string;
    const api = ApiService.getApi();
    const request = {
      partId: part.id,
      groupId: part.groupInstanceId,
      templateTypeId: getState().system?.templateTypeId,
      isReplacementPart: false,
    };
    await api.partsSizingDataCreate(partId, request);
    await SystemService.updateLinkByEndPartId(partId);
  },

  updateNameAndDescription: async (name: string, description: string | null) => {
    const system = getState().system;
    const systemData = buildUpdateData(system);
    systemData.name = name;
    systemData.description = description;
    const status = await SystemService.update(systemData);
    return status;
  },

  updatePartName: async (partId: string, partName: string) => {
    const systemCopy = { ...getState().system };
    const partToUpdate = systemCopy.parts?.find((part) => part.id === partId) as Part;
    const api = ApiService.getApi();
    const response = await api.partsUpdate(partId, {
      partId: partId,
      name: partName,
      groupInstanceId: partToUpdate.groupInstanceId,
      order: partToUpdate.order,
    });
    if (response.status === 200) {
      partToUpdate.name = partName;
      setState({ system: systemCopy });
    }
  },

  updatePartMetadata: async (partId: string, metadataInstances: Metadata[]) => {
    const systemCopy = { ...getState().system };
    const partToUpdate = systemCopy.parts?.find((part) => part.id === partId) as Part;
    const api = ApiService.getApi();
    const response = await api.partsUpdate(partId, {
      partId: partId,
      name: partToUpdate.name,
      groupInstanceId: partToUpdate.groupInstanceId,
      order: partToUpdate.order,
      metadataInstances: metadataInstances,
    });
    if (response.status === 200) {
      setState({ system: systemCopy });
    }
  },

  updatePartWorkflow: async (id: string, data: UpdateWorkflowRequest, partId: string) => {
    const api = ApiService.getApi();
    const response = await api.workflowUpdate(id, data);
    if (response.status === 200) {
      const systemCopy = { ...getState().system };
      const partToUpdate = systemCopy.parts?.find((part) => part.id === partId) as Part;
      const workflowStepToUpdate = partToUpdate?.workflowSteps?.find((step) => step.id === data.stepId) as WorkflowStepStatus;
      workflowStepToUpdate.isCompleted = true;
      setState({ system: systemCopy });
      return true;
    }
    return false;
  },

  updateGroupName: async (groupId: number, groupName: string) => {
    const api = ApiService.getApi();
    const response = await api.groupsUpdate(groupId, {
      groupId: groupId,
      name: groupName,
    });
    if (response.status === 200) {
      const systemCopy = { ...getState().system };
      const groupToUpdate = systemCopy.groups?.find((group) => group.id === groupId) as Group;
      groupToUpdate.name = groupName;
      setState({ system: systemCopy });
    }
  },

  updateAirflowDirection: async (direction: boolean | undefined) => {
    const system = getState().system;
    const systemData = buildUpdateData(system);
    systemData.isRightToLeft = direction;
    const status = await SystemService.update(systemData);
    return status;
  },

  updateSystemUnits: async (useMetric: boolean) => {
    const system = getState().system;
    const systemData = buildUpdateData(system);
    systemData.displayInMetricUnits = useMetric;
    await SystemService.update(systemData);
  },

  undoDelete: async (id: string) => {
    const api = ApiService.getApi();
    const deleteData = {
      id: id,
      isDeleted: false,
    };
    const response = await api.systemsUpdate(id, deleteData);
    return response.data.deleted;
  },

  delete: async (id: string) => {
    const api = ApiService.getApi();
    const deleteData = {
      id: id,
      isDeleted: true,
    };
    const response = await api.systemsUpdate(id, deleteData);
    return response.data.deleted;
  },

  getPartCategoriesList: async (
    templateTypeId: number,
    PreviousPartCategoryId?: number,
    GroupDefinitionId?: number,
    PreviousPartCategoryName?: string,
    PreviousGroupDefinitionUniqueName?: string,
    CurrentGroupDefinitionUniqueName?: string,
  ) => {
    const api = ApiService.getApi();
    const partCategoriesList = await api.groupedPartCategoriesList({
      TemplateTypeId: templateTypeId,
      PreviousPartCategoryId: PreviousPartCategoryId,
      GroupDefinitionId: GroupDefinitionId,
      PreviousPartCategoryName: PreviousPartCategoryName,
      PreviousGroupDefinitionUniqueName: PreviousGroupDefinitionUniqueName,
      CurrentGroupDefinitionUniqueName: CurrentGroupDefinitionUniqueName,
    });
    return partCategoriesList.data as GetGroupedPartCategoriesResponse;
  },

  setGroups: async (groups: Group[] | null) => {
    const systemCopy = { ...getState().system };
    systemCopy.groups = groups;
    setState({ system: systemCopy });
  },

  setParts: async (parts: Part[] | null) => {
    const systemCopy = { ...getState().system };
    systemCopy.parts = parts;
    setState({ system: systemCopy });
  },

  handleValvesInState: (systemCopy: GetSystemResponse, createManifoldRequest: CreateManifoldRequest, responseData: CreateManifoldResponse) => {
    // Handlle all parts (valves)
    const parts = getState().system?.parts;
    createManifoldRequest.valves?.map((valveRequest, index) => {
      const valve = Object.assign(valveRequest, responseData.valves && responseData.valves[index]);
      parts?.push(valve);
    });
    systemCopy.parts = parts;

    // Handle links and virtual groups
    systemCopy.overlapGroupLinks?.push(...(responseData.overlapGroupLinks as OverlapGroupLinkResponse[]));
    systemCopy.virtualGroups?.push(...(responseData.virtualGroups as VirtualGroup[]));

    return systemCopy;
  },

  createManifold: async (createManifoldRequest: CreateManifoldRequest, hasExistingLinkedPart?: boolean) => {
    const api = ApiService.getApi();
    const response = await api.manifoldsCreate(createManifoldRequest);

    if (response.status === 200) {
      const systemCopy = { ...getState().system } as GetSystemResponse;
      // Handle all groups
      const manifoldGroup = Object.assign(createManifoldRequest.manifoldGroup as CreateGroupRequest, response.data.manifoldGroup) as Group;
      const segmentGroup = Object.assign(createManifoldRequest?.segmentGroup as CreateGroupRequest, response.data.segmentGroup) as Group;

      // for each application group
      const applicationGroups = createManifoldRequest.overlappingGroups?.map((createApplicationGroupRequest, i) =>
        Object.assign(
          createApplicationGroupRequest,
          response.data.overlappingGroups && response.data.overlappingGroups.length > i && response.data.overlappingGroups[i + (hasExistingLinkedPart ? 1 : 0)],
        ),
      ) as Group[];

      const groups = getState().system?.groups;
      applicationGroups.map((applicationGroup) => groups?.push(applicationGroup));
      groups?.push(manifoldGroup, segmentGroup);
      systemCopy.groups = groups;

      const manifoldPart = Object.assign(createManifoldRequest.manifold as CreatePartRequest, response.data.manifold) as Part;
      systemCopy.parts?.push(manifoldPart);

      // Finally, replace the current system with the updated copy
      setState({
        system: SystemService.handleValvesInState(systemCopy, createManifoldRequest, response.data),
      });
    }

    return response;
  },

  createGroup: async (createGroupRequest: CreateGroupRequest) => {
    const api = ApiService.getApi();
    const response = await api.groupsCreate(createGroupRequest);
    if (response.status === 200) {
      const groupToAdd = Object.assign(createGroupRequest, response.data);
      const groups = getState().system?.groups;
      groups?.push(groupToAdd);
      SystemService.setGroups(groups as Group[]);
    }
    return response;
  },

  createPart: async (createPartRequest: CreatePartRequest) => {
    let addedPart: Part = {};
    const api = ApiService.getApi();
    const response = await api.partsCreate(createPartRequest);

    if (response.status === 200) {
      const systemCopy = { ...getState().system };

      addedPart = Object.assign(createPartRequest, response.data);

      const partsInGroup = systemCopy?.parts?.filter((part) => part.groupInstanceId === createPartRequest.groupInstanceId);
      const existingLastPart = partsInGroup ? sortByOrder(partsInGroup)[partsInGroup.length - 1] : undefined;

      if (systemCopy.virtualGroups?.some((group) => group.baseGroupId === addedPart.groupInstanceId)) {
        //update overlap grouplink
        const overlappingGroup = systemCopy.overlapGroupLinks?.find(
          (groupLink) => groupLink.overlappingGroupId === createPartRequest.groupInstanceId,
        ) as OverlapGroupLinkResponse;
        overlappingGroup.linkedPartId = addedPart.id;

        //update virtual group
        const virtualGroup = systemCopy.virtualGroups?.find((group) => group.baseGroupId === createPartRequest.groupInstanceId);
        virtualGroup?.linkedPartIds?.push(addedPart.id as string);
      } else if (existingLastPart) {
        const associatedGroup = systemCopy.groups?.find((group) => group.id === createPartRequest.groupInstanceId) as Group;
        associatedGroup.isFinished = createPartRequest.groupIsFinished;

        const partLinkCreateRequest: CreatePartLinkRequest = {
          startChipId: existingLastPart?.chipId,
          endChipId: addedPart?.chipId,
          linkState: LinkState.Initial,
        };
        const partLinkCreateResponse = await api.partLinksCreate(partLinkCreateRequest);

        if (partLinkCreateResponse) {
          const partLinkToAdd: PartLink = Object.assign(partLinkCreateRequest, partLinkCreateResponse.data);
          partLinkToAdd.startId = existingLastPart?.id;
          partLinkToAdd.startChipColumn = existingLastPart?.column;
          partLinkToAdd.startChipRow = existingLastPart?.row;
          partLinkToAdd.endId = addedPart?.id;
          partLinkToAdd.endChipColumn = addedPart?.column;
          partLinkToAdd.endChipRow = addedPart?.row;
          partLinkToAdd.state = partLinkCreateRequest.linkState;
          systemCopy.partLinks?.push(partLinkToAdd);
        }

        const groups = systemCopy.groups?.filter((group) => group.id !== createPartRequest.groupInstanceId);
        groups?.push(associatedGroup);
        systemCopy.groups = groups;
      }

      const parts = systemCopy?.parts;
      parts?.push(addedPart);
      systemCopy.parts = parts;
      setState({ system: systemCopy });
    }

    return addedPart;
  },

  addValveToExistingManifold: async (id: number, data: AddValveToManifoldRequest) => {
    const systemCopy = { ...getState().system } as GetSystemResponse;
    const api = ApiService.getApi();
    const response = await api.addValveToManifold(id, data);
    if (response.status === 200) {
      systemCopy.parts?.push(response.data.valve as Part);
      systemCopy.overlapGroupLinks?.push(response.data.link as OverlapGroupLinkResponse);
      systemCopy.virtualGroups?.push(response.data.virtualGroup as VirtualGroup);
      setState({ system: systemCopy });
    }
  },

  updateOverlapGroupLink: async (overlapGroupLinkRequest: UpdateOverlapGroupLinkRequest) => {
    const systemCopy = { ...getState().system } as GetSystemResponse;
    const api = ApiService.getApi();
    const response = await api.overlapGroupLinksUpdate(overlapGroupLinkRequest.id as number, overlapGroupLinkRequest);
    if (response.status === 200) {
      const responseData = response.data as UpdateOverlapGroupLinkResponse;
      const overlapGroupLink = systemCopy.overlapGroupLinks?.find((link) => link.id === overlapGroupLinkRequest.id);
      if (overlapGroupLink) {
        overlapGroupLink.linkedPartId = overlapGroupLinkRequest.linkedPartId;
      }

      const virtualGroup = systemCopy.virtualGroups?.find((group) => group.baseGroupId === responseData.virtualGroup?.baseGroupId);
      if (virtualGroup && overlapGroupLinkRequest.linkedPartId && !virtualGroup.linkedPartIds?.some((id) => id === overlapGroupLinkRequest.linkedPartId)) {
        virtualGroup.linkedPartIds?.push(overlapGroupLinkRequest.linkedPartId);
      }

      setState({ system: systemCopy });
    }
  },

  revertManifold: async (revertManifoldRequest: RevertManifoldRequest) => {
    const api = ApiService.getApi();
    const response = await api.revertOptimization(String(revertManifoldRequest.manifoldGroupId), revertManifoldRequest);
    if (response.status === 200) {
      const systemId = getState().system?.id as string;
      const refreshed = await SystemService.refreshSystemById(systemId);
      return Promise.resolve(refreshed);
    }
    return Promise.resolve(false);
  },

  deleteManifoldWithVirtualGroups: async (manifoldGroupId: number) => {
    const api = ApiService.getApi();
    const withApplicationGroups = true;
    const response = await api.manifoldsDelete(manifoldGroupId, { includeOverlappingGroups: withApplicationGroups });
    if (response.status === 200) {
      deleteManifold(manifoldGroupId, withApplicationGroups);
    }
  },

  disconnectFromManifold: async (
    overlapGroupLink: OverlapGroupLinkResponse,
    setShowOptimizedManifoldAlert: React.Dispatch<React.SetStateAction<boolean>>,
    setManifoldActionName: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    setManifoldActionName('disconnect');
    const manifoldGroupId = getState().system?.groups?.find((group) => group.id === overlapGroupLink.intersectedGroupId)?.parentGroupId as number;
    const overlappingApplicationGroup = getState().system?.groups?.find((group) => group.id === overlapGroupLink.overlappingGroupId);
    if (isManifoldOptimized(manifoldGroupId)) {
      setShowOptimizedManifoldAlert(true);
      return;
    }

    const api = ApiService.getApi();
    const response = await api.disconnectGroupFromManifold(manifoldGroupId, overlappingApplicationGroup?.id as number);
    if (response.status === 200) {
      const systemCopy = { ...getState().system };
      /** Removing the disconnected element from the virtual group array */
      systemCopy.virtualGroups = systemCopy.virtualGroups?.filter((group) => group.baseGroupId !== overlapGroupLink.overlappingGroupId);
      /** Removing the disconnected element link from the overlapGroupLinks array */
      systemCopy.overlapGroupLinks = systemCopy.overlapGroupLinks?.filter(
        (overlapGroup) => overlapGroup.overlappingGroupId !== overlapGroupLink.overlappingGroupId,
      );
      /** Creating a new partlink for the disconnected element */
      const partLinkToAdd: PartLink = response.data.partLink as PartLink;
      systemCopy.partLinks?.push(partLinkToAdd);
      /** Merging the properties of the part array elements corresponding to the stand-alone valve */
      const partToUpdate: Part = systemCopy.parts?.find((part) => part.id == overlapGroupLink.intersectedPartId) as Part;
      partToUpdate.workflowId = response.data.standAloneValve?.workflowId;
      partToUpdate.groupInstanceId = response.data.standAloneValve?.groupInstanceId;
      partToUpdate.row = response.data.standAloneValve?.row;
      partToUpdate.column = (response.data.standAloneValve?.column as number) + 1;
      partToUpdate.order = (response.data.standAloneValve?.order as number) + 1;
      partToUpdate.workflowSteps = response.data.standAloneValve?.workflowSteps;
      /** Updating Row and Column Order */
      response.data.updatedValvesChips.forEach((chip) => {
        const updatedPart = systemCopy.parts?.find((part) => part.chipId === chip.id) as Part;
        updatedPart.row = chip.row;
        updatedPart.order = chip.order;
      });
      /**Updating IsFinished Property */
      const groupToUpdate = systemCopy.groups?.find((group) => group.id == overlappingApplicationGroup?.id) as Group;
      groupToUpdate.isFinished = response.data.overlappingGroupIsFinished;

      setState({ system: systemCopy });
    }
  },

  deleteValveFromManifold: async (
    manifoldId: number,
    valveId: string,
    linkedGroupId: number,
    setShowOptimizedManifoldAlert: React.Dispatch<React.SetStateAction<boolean>>,
    setShowDeleteManifoldValidation: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (isManifoldOptimized(manifoldId)) {
      setShowOptimizedManifoldAlert(true);
      return;
    }
    const manifoldLinks = getState().system?.overlapGroupLinks?.find(
      (overlapGroupLink) => overlapGroupLink.overlappingGroupId === linkedGroupId,
    ) as OverlapGroupLinkResponse;
    const valves = getState().system?.parts?.filter((part) => manifoldLinks.intersectedGroupId === part.groupInstanceId) as Part[];
    if (valves.length === 2) {
      setShowDeleteManifoldValidation(true);
      return;
    }
    const api = ApiService.getApi();
    const response = await api.deleteValveFromManifold(manifoldId, valveId);
    if (response.status === 200) {
      const updatedSystem = getUpdatedGroupInState(linkedGroupId);
      if (updatedSystem) {
        //remove valve
        const parts = updatedSystem.parts?.filter((part) => part.id !== valveId);
        //update row/order
        response.data.updatedValvesChips.forEach((chip) => {
          const partToUpdate = parts?.find((part) => part.chipId === chip.id) as Part;
          partToUpdate.row = chip.row;
          partToUpdate.order = chip.order;
        });
        updatedSystem.parts = parts;
        setState({ system: updatedSystem });
      }
    }
  },

  deleteGroup: async (id: number) => {
    const api = ApiService.getApi();
    const groupDefinitionId = getState().system?.groups?.find((group) => group.id === id)?.definitionId;
    const isManifold =
      getState().system?.groupDefinitions?.find((groupDefinition) => groupDefinition.id === groupDefinitionId)?.uniqueName === 'PneumaticSupplyManifolds';
    const response = isManifold ? await api.manifoldsDelete(id) : await api.groupsDelete(id);
    if (response.status === 200) {
      if (isManifold) {
        deleteManifold(id, false);
      } else {
        const updatedSystem = getUpdatedGroupInState(id);
        if (updatedSystem) setState({ system: updatedSystem });
      }
    }
  },

  deletePart: async (id: string) => {
    const api = ApiService.getApi();
    const response = await api.partsDelete(id);
    if (response.status === 200) {
      const deletedPart = getState().system?.parts?.find((part) => part.id === id);
      const parts = getState().system?.parts?.filter((part) => part.groupInstanceId === deletedPart?.groupInstanceId) as Part[];
      parts.sort((a, b) => (a.column as number) - (b.column as number));
      const deletedPartIndex = parts.findIndex((part) => part.id === id);
      if (deletedPartIndex === parts.length - 1) {
        let groupedPartCategoriesResponse;
        let currentGroup: Group;
        if (parts.length > 1) {
          const previousPart = parts[deletedPartIndex - 1];
          const previousPartCategory = getState().system?.partCategories?.find((partCategory) => partCategory.id === previousPart.categoryId);
          currentGroup = getState().system?.groups?.find((group) => group.id === previousPart.groupInstanceId) as Group;
          const groupDefinition = getState().system?.groupDefinitions?.find((definition) => definition.id === currentGroup?.definitionId);
          groupedPartCategoriesResponse = await SystemService.getPartCategoriesList(
            getState().system?.templateTypeId as number,
            previousPartCategory?.id,
            groupDefinition?.id,
            previousPartCategory?.name as string,
            undefined,
            groupDefinition?.uniqueName as string,
          );

          const partLinks = getState().system?.partLinks?.filter((partLink) => partLink.startId !== previousPart.id);

          const systemCopy = { ...getState().system };
          systemCopy.partLinks = partLinks;
          setState({ system: systemCopy });
        } else {
          currentGroup = getState().system?.groups?.find((group) => group.id === parts[deletedPartIndex].groupInstanceId) as Group;
          const groupDefinition = getState().system?.groupDefinitions?.find((definition) => definition.id === currentGroup?.definitionId);
          groupedPartCategoriesResponse = await SystemService.getPartCategoriesList(
            getState().system?.templateTypeId as number,
            undefined,
            groupDefinition?.id,
            undefined,
            undefined,
            groupDefinition?.uniqueName as string,
          );
        }
        const partCategoriesToAdd: PartCategoryToAdd[] = [];
        groupedPartCategoriesResponse.groupedPartCategories?.forEach((groupedPartCategory) =>
          partCategoriesToAdd.push(...(groupedPartCategory.partCategories as PartCategoryToAdd[])),
        );
        const isLastPartInGroup = partCategoriesToAdd.find(
          (partCategory) => partCategory.partCategoryId === parts[deletedPartIndex].categoryId,
        )?.isLastPartInGroup;
        if (isLastPartInGroup && currentGroup.isFinished) {
          await api.groupsUpdate(currentGroup.id as number, {
            groupId: currentGroup.id,
            name: currentGroup?.name,
            isFinished: false,
          });
          const systemCopy = { ...getState().system };
          const groupToUpdate = systemCopy.groups?.find((group) => group.id === currentGroup.id) as Group;
          groupToUpdate.isFinished = false;
          setState({ system: systemCopy });
        }
      }
      const partsToKeep = getState().system?.parts?.filter((partObj) => partObj.id !== id);
      SystemService.setParts(partsToKeep as Part[]);
    }
  },

  deleteValve: async (id: string) => {
    const api = ApiService.getApi();
    const response = await api.partsDelete(id);
    if (response.status === 200) {
      const system = getState()?.system as GetSystemResponse;
      const parts = system.parts?.filter((part) => part.id !== id);

      const systemCopy = { ...getState().system };
      systemCopy.parts = parts;
      setState({ system: systemCopy });
    }
  },

  getPartsDetail: async (id: string) => {
    const api = ApiService.getApi();
    const response = await api.partsDetail(id);
    if (response.status === 200) {
      const parts = getState().system?.parts?.filter((part) => part.id !== id);
      parts?.push(response.data);
      SystemService.setParts(parts as Part[]);
    }
  },

  getMySystemsAndProjects: async () => {
    const api = ApiService.getApi();
    const response = await api.mySystemsAndProjects();
    if (response.status === 200) {
      return response.data;
    }
  },
  getSystemTypesAndTemplates: async () => {
    const api = ApiService.getApi();
    const response = await api.systemTypesAndTemplates();
    if (response.status === 200) {
      return response.data;
    }
  },

  createSystem: async (createSystemRequest: CreateSystemRequest) => {
    const api = ApiService.getApi();
    const response = await api.systemsCreate(createSystemRequest);
    return response;
  },

  updateSystemInState: (systemCopy: GetSystemResponse) => {
    setState({ system: systemCopy });
  },

  getSystemShares: async (systemId: string) => {
    const api = ApiService.getApi();
    const response = await api.getSystemShares(systemId);
    if (response.status === 200) {
      return response.data;
    }
  },

  saveSystemShares: async (systemId: string, request: SaveSystemSharesRequest) => {
    const api = ApiService.getApi();
    const response = await api.saveSystemShares(systemId, request);
    if (response.status === 200) {
      return response.data;
    }
  },
};
export default SystemService;
